import { createContext, useContext, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Show, useAuth, useClerk } from "@clerk/react";
import { ShieldAlert, Loader2 } from "lucide-react";
import { API, authed } from "@/lib/api";
import { applyBrand } from "@/context/ConfigContext";

const MeContext = createContext(null);
export const useMe = () => useContext(MeContext);

export const Denied = ({ title = "Admin access only", text }) => {
  const { signOut } = useClerk();
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-brand-soft" data-testid="admin-access-denied">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-sm border">
        <ShieldAlert className="w-10 h-10 mx-auto text-red-500 mb-3" />
        <h1 className="text-2xl font-semibold mb-2">{title}</h1>
        <p className="text-sm text-stone-500 mb-6">{text || "This account doesn't have access here. Ask the platform admin to add your email to your school."}</p>
        <div className="flex gap-3 justify-center">
          <Link to="/" className="px-4 py-2 rounded-full border text-sm" data-testid="denied-home-link">Back to home</Link>
          <button onClick={() => signOut({ redirectUrl: "/sign-in" })} className="px-4 py-2 rounded-full btn-brand text-sm" data-testid="denied-signout-button">
            Switch account
          </button>
        </div>
      </div>
    </div>
  );
};

function RoleCheck({ superOnly, children }) {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me`, await authed(getToken));
        setMe(data);
      } catch {
        setMe({ role: "error" });
      }
    })();
  }, [getToken]);

  useEffect(() => { if (superOnly) applyBrand(null); }, [superOnly]);

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-6 h-6 animate-spin text-brand" />
      </div>
    );
  }
  if (me.role === "error" || me.role === "none") return <Denied />;
  if (superOnly && me.role !== "super_admin") return <Denied title="Super admin only" text="Only the platform owner can manage all schools." />;
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}

export default function AdminGuard({ superOnly = false, children }) {
  return (
    <>
      <Show when="signed-in"><RoleCheck superOnly={superOnly}>{children}</RoleCheck></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
